import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Save, Upload, Trash2, Palette } from "lucide-react";
import api from "../../services/api";

interface CompanyData {
  id: number;
  name: string;
  slug: string;
  country: string;
  settings: {
    industry?: string;
    timezone?: string;
    currency?: string;
    language?: string;
    complianceProfile?: string;
    logoUrl?: string;
    logoFileId?: string;
    primaryColor?: string;
    accentColor?: string;
  };
}

const COUNTRIES = [
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "CZ", name: "Czech Republic" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "IE", name: "Ireland" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
  { code: "NO", name: "Norway" },
  { code: "PL", name: "Poland" },
  { code: "ES", name: "Spain" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
];

const INDUSTRIES = [
  "Technology",
  "Finance",
  "Healthcare",
  "Manufacturing",
  "Retail",
  "Consulting",
  "Education",
  "Government",
  "Media",
  "Other",
];

const TIMEZONES = [
  "UTC",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Warsaw",
  "Europe/Amsterdam",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
];

const CURRENCIES = [
  "EUR",
  "USD",
  "GBP",
  "CHF",
  "PLN",
  "SEK",
  "NOK",
  "DKK",
  "CZK",
];

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "de", name: "German" },
  { code: "fr", name: "French" },
  { code: "pl", name: "Polish" },
  { code: "nl", name: "Dutch" },
  { code: "es", name: "Spanish" },
];

const CompanySettingsTab: React.FC = () => {
  const { t } = useTranslation("admin");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [industry, setIndustry] = useState("");
  const [timezone, setTimezone] = useState("");
  const [currency, setCurrency] = useState("");
  const [language, setLanguage] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#1E40AF");
  const [accentColor, setAccentColor] = useState("#F59E0B");
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = (await api.tenant.getSettings()) as {
        success: boolean;
        company: CompanyData;
      };
      if (result.success && result.company) {
        const c = result.company;
        setName(c.name || "");
        setCountry(c.country || "");
        setIndustry(c.settings?.industry || "");
        setTimezone(c.settings?.timezone || "");
        setCurrency(c.settings?.currency || "");
        setLanguage(c.settings?.language || "");
        setLogoUrl(c.settings?.logoUrl || null);
        setPrimaryColor(c.settings?.primaryColor || "#1E40AF");
        setAccentColor(c.settings?.accentColor || "#F59E0B");
      }
    } catch (error) {
      console.error("Error loading company settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      await api.tenant.updateSettings({
        name,
        country,
        settings: {
          industry,
          timezone,
          currency,
          language,
          primaryColor,
          accentColor,
        },
      });
      setSaveMessage({
        type: "success",
        text: t("company.messages.saveSuccess"),
      });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error("Error saving company settings:", error);
      setSaveMessage({
        type: "error",
        text: t("company.messages.saveError"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoUploading(true);
    try {
      const result = (await api.tenant.uploadLogo(file)) as {
        success: boolean;
        logoUrl: string;
      };
      if (result.success) {
        setLogoUrl(result.logoUrl);
        setSaveMessage({
          type: "success",
          text: t("company.messages.logoUploadSuccess"),
        });
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      setSaveMessage({ type: "error", text: "Failed to upload logo." });
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleLogoRemove = async () => {
    try {
      await api.tenant.deleteLogo();
      setLogoUrl(null);
      setSaveMessage({ type: "success", text: "Logo removed successfully!" });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error("Error removing logo:", error);
      setSaveMessage({ type: "error", text: "Failed to remove logo." });
    }
  };

  if (loading) {
    return <div className="loading">Loading company settings...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>
          <Building2 size={16} /> Company Profile
        </h2>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {saveMessage && (
        <div
          className={`${saveMessage.type === "success" ? "success-message" : "error-message"}`}
          style={{
            padding: "8px 12px",
            borderRadius: "4px",
            marginBottom: "16px",
            backgroundColor:
              saveMessage.type === "success" ? "#dcfce7" : "#fee2e2",
            color: saveMessage.type === "success" ? "#166534" : "#991b1b",
          }}
        >
          {saveMessage.text}
        </div>
      )}

      <div className="config-section">
        <div className="form-group">
          <label htmlFor="company-name" className="form-label">
            Company Name
          </label>
          <input
            id="company-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-input"
            placeholder="Your company name"
          />
        </div>

        <div
          className="form-row"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          <div className="form-group">
            <label htmlFor="company-country" className="form-label">
              Country
            </label>
            <select
              id="company-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="form-input"
            >
              <option value="">Select country...</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="company-industry" className="form-label">
              Industry
            </label>
            <select
              id="company-industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="form-input"
            >
              <option value="">Select industry...</option>
              {INDUSTRIES.map((ind) => (
                <option key={ind} value={ind}>
                  {ind}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          className="form-row"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "16px",
          }}
        >
          <div className="form-group">
            <label htmlFor="company-timezone" className="form-label">
              Timezone
            </label>
            <select
              id="company-timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="form-input"
            >
              <option value="">Select timezone...</option>
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="company-currency" className="form-label">
              Currency
            </label>
            <select
              id="company-currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="form-input"
            >
              <option value="">Select currency...</option>
              {CURRENCIES.map((cur) => (
                <option key={cur} value={cur}>
                  {cur}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="company-language" className="form-label">
              Language
            </label>
            <select
              id="company-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="form-input"
            >
              <option value="">Select language...</option>
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Company Logo */}
        <div
          style={{
            marginTop: "24px",
            borderTop: "1px solid #E5E7EB",
            paddingTop: "20px",
          }}
        >
          <h3
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
            }}
          >
            <Upload size={16} /> Company Logo
          </h3>
          <p
            style={{
              color: "#6B7280",
              fontSize: "0.875rem",
              marginBottom: "12px",
            }}
          >
            Upload a company logo (PNG, JPEG, SVG, or WebP, max 2MB). This will
            be shown on the login page and emails.
          </p>

          {logoUrl && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "12px",
              }}
            >
              <img
                src={logoUrl}
                alt="Company logo"
                style={{
                  maxHeight: "64px",
                  maxWidth: "200px",
                  objectFit: "contain",
                  border: "1px solid #E5E7EB",
                  borderRadius: "4px",
                  padding: "4px",
                }}
              />
              <button
                className="btn-secondary"
                onClick={handleLogoRemove}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: "#DC2626",
                }}
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          )}

          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            onChange={handleLogoUpload}
            disabled={logoUploading}
            style={{ fontSize: "0.875rem" }}
          />
          {logoUploading && (
            <span style={{ marginLeft: "8px", color: "#6B7280" }}>
              Uploading...
            </span>
          )}
        </div>

        {/* Branding Colors */}
        <div
          style={{
            marginTop: "24px",
            borderTop: "1px solid #E5E7EB",
            paddingTop: "20px",
          }}
        >
          <h3
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
            }}
          >
            <Palette size={16} /> Branding
          </h3>
          <p
            style={{
              color: "#6B7280",
              fontSize: "0.875rem",
              marginBottom: "12px",
            }}
          >
            Customize your company's branding colors. These will be applied to
            the login page when accessed via your company URL.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <div className="form-group">
              <label htmlFor="primary-color" className="form-label">
                Primary Color
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="color"
                  id="primary-color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  style={{
                    width: "40px",
                    height: "36px",
                    padding: "2px",
                    cursor: "pointer",
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px",
                  }}
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="form-input"
                  placeholder="#1E40AF"
                  style={{ flex: 1 }}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="accent-color" className="form-label">
                Accent Color
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="color"
                  id="accent-color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  style={{
                    width: "40px",
                    height: "36px",
                    padding: "2px",
                    cursor: "pointer",
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px",
                  }}
                />
                <input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="form-input"
                  placeholder="#F59E0B"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettingsTab;
